import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  ClipboardList,
  CalendarDays,
  Mail,
  Video,
  ExternalLink,
  Clock,
  Briefcase,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { AttendeeList, type Attendee } from "@/components/AttendeeList";
import { PlaneRefreshHeader } from "@/components/PlaneRefreshHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface TicketLite {
  id: string;
  name: string;
  state: string;
  stateGroup?: "backlog" | "unstarted" | "started" | "completed" | "cancelled" | "unknown";
  priority: string;
  dueDate?: string;
  url: string;
  projectName?: string;
  projectIdentifier?: string;
  panelProjectName?: string;
}

interface TeamProfile {
  user: {
    id: string;
    email: string;
    name: string;
    role: "admin" | "finanzas" | "lead" | "member";
    position: string | null;
    active: boolean;
    createdAt: number;
  };
  tickets: {
    overdue: TicketLite[];
    upcoming: TicketLite[];
    open: TicketLite[];
  };
  ticketsMeta: { cachedAt: number; source: "manual" | "auto" } | null;
  projects: Array<{
    id: string;
    name: string;
    slug: string;
    status: string;
    health: string;
    type: string;
    roleInProject: string;
  }>;
  upcomingEvents: Array<{
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    startTime: string | null;
    endTime: string | null;
    source: "manual" | "google_calendar";
    notes: string | null;
    externalMeetUrl: string | null;
    externalHtmlLink: string | null;
    externalVisibility: "default" | "public" | "private" | "confidential" | null;
    externalAttendees: Attendee[] | null;
  }>;
  recentStandups: Array<{ id: string; date: string; preview: string[] }>;
}

const ROLE_LABEL = {
  admin: "Admin",
  finanzas: "Finanzas",
  lead: "Lead",
  member: "Equipo",
} as const;
const ROLE_BADGE = {
  admin: "badge-warning",
  finanzas: "badge-success",
  lead: "badge-primary",
  member: "badge-ghost",
} as const;
const HEALTH_RING: Record<string, string> = {
  green: "bg-success ring-success/30",
  yellow: "bg-warning ring-warning/30",
  red: "bg-error ring-error/30",
  unknown: "bg-base-300 ring-base-300/30",
};

const STATE_BADGE_BY_GROUP: Record<NonNullable<TicketLite["stateGroup"]>, string> = {
  backlog: "badge-ghost",
  unstarted: "badge-info",
  started: "badge-primary",
  completed: "badge-success",
  cancelled: "badge-warning",
  unknown: "badge-ghost",
};

export const Route = createFileRoute("/team/$userId/")({
  component: TeamProfilePage,
});

function initials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

function daysFromToday(iso: string): number {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return 999;
  const [, y, mo, d] = m;
  const eventDay = new Date(parseInt(y!, 10), parseInt(mo!, 10) - 1, parseInt(d!, 10));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((eventDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function relativeDay(iso: string): string {
  const d = daysFromToday(iso);
  if (d === 0) return "Hoy";
  if (d === 1) return "Mañana";
  if (d < 7) return "Esta semana";
  return "Más adelante";
}

function TeamProfilePage() {
  const { userId } = Route.useParams();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<TeamProfile>({
    queryKey: ["team", userId],
    queryFn: () => api.get(`/team/${userId}`),
  });
  const refreshTickets = useMutation({
    mutationFn: () => api.get<TeamProfile>(`/team/${userId}?refresh=1`),
    onSuccess: (fresh) => qc.setQueryData(["team", userId], fresh),
  });

  // Auto-refresh una sola vez si el primer fetch trajo 0 tickets. El
  // cache de Plane puede estar expirado en cold start; un refresh
  // silencioso evita que la vista se quede en cero sin razón. Si el user
  // no tiene Plane vinculado el backend responde rápido con vacío y no
  // pasa nada. Mismo patrón que /my-tickets y /index.
  const autoRefreshedRef = useRef<string>("");
  useEffect(() => {
    if (!data) return;
    if (autoRefreshedRef.current === userId) return;
    const ticketCount =
      data.tickets.overdue.length + data.tickets.upcoming.length + data.tickets.open.length;
    if (ticketCount > 0) return;
    autoRefreshedRef.current = userId;
    refreshTickets.mutate();
  }, [data, userId, refreshTickets]);

  if (isLoading) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="card-elevated p-10 text-center text-base-content/55">
        No pudimos cargar el perfil. ¿La cuenta sigue activa?
      </div>
    );
  }

  const { user, projects, upcomingEvents, recentStandups, tickets } = data;
  const ticketCount = tickets.overdue.length + tickets.upcoming.length + tickets.open.length;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="compañero"
        title={user.name}
        subtitle={user.email}
        actions={
          <Link to="/team" className="btn btn-ghost btn-sm">
            <ArrowLeft className="size-4" />
            Volver
          </Link>
        }
      />

      {/* Identidad */}
      <section className="card-elevated p-5 flex items-center gap-4 flex-wrap">
        <div className="size-14 rounded-full bg-primary/15 text-primary grid place-items-center font-display font-semibold text-lg">
          {initials(user.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-lg leading-tight">{user.name}</div>
          <div className="text-sm text-base-content/70 inline-flex items-center gap-1.5 mt-0.5">
            <Briefcase className="size-3.5 text-base-content/50" />
            {user.position ?? <span className="italic text-base-content/45">Sin puesto</span>}
          </div>
          <div className="text-xs text-base-content/55 inline-flex items-center gap-2 mt-0.5">
            <Mail className="size-3" />
            <a href={`mailto:${user.email}`} className="hover:text-primary truncate">
              {user.email}
            </a>
          </div>
        </div>
        {user.role !== "member" && (
          <span className={`badge ${ROLE_BADGE[user.role]}`}>{ROLE_LABEL[user.role]}</span>
        )}
      </section>

      {/* Tickets del compañero — mismo bucketing que el dashboard. */}
      <section className="card-elevated p-4 md:p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 inline-flex items-center gap-2">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            Tickets
            <span className="text-base-content/45 font-normal normal-case tracking-normal">
              · {ticketCount}
            </span>
          </h3>
          <div className="flex items-center gap-3 flex-wrap">
            <PlaneRefreshHeader
              meta={data.ticketsMeta}
              isPending={refreshTickets.isPending}
              onRefresh={() => refreshTickets.mutate()}
            />
            {ticketCount > 0 && (
              <Link
                to="/team/$userId/tickets"
                params={{ userId }}
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                Ver todos <ArrowUpRight className="size-3" />
              </Link>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4">
          <TeamTicketBucket
            label="Atrasados"
            tone="error"
            tickets={tickets.overdue}
            empty="Sin atrasados"
            loading={refreshTickets.isPending}
          />
          <TeamTicketBucket
            label="Próximos · 3 días"
            tone="warning"
            tickets={tickets.upcoming}
            empty="Nada inminente"
            loading={refreshTickets.isPending}
          />
          <TeamTicketBucket
            label="Abiertos"
            tone="primary"
            tickets={tickets.open}
            empty="Inbox limpio"
            loading={refreshTickets.isPending}
          />
        </div>
      </section>

      {/* Grid: Proyectos (izq) + Eventos (der) */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 items-start">
        <section className="xl:col-span-2 space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 inline-flex items-center gap-2">
            <span className="size-1.5 rounded-full bg-info" aria-hidden />
            Proyectos · {projects.length}
          </h3>
          {projects.length === 0 ? (
            <div className="card-elevated p-5 text-sm text-base-content/55 text-center">
              No participa en ningún proyecto.
            </div>
          ) : (
            <ul className="card-elevated divide-y divide-base-300/60">
              {projects.map((p) => (
                <li key={p.id}>
                  <Link
                    to="/projects/$projectId"
                    params={{ projectId: p.id }}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-base-200/60 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        aria-hidden
                        title={`Salud: ${p.health}`}
                        className={`size-2.5 rounded-full ring-4 ${
                          HEALTH_RING[p.health] ?? HEALTH_RING.unknown
                        } shrink-0`}
                      />
                      <div className="min-w-0">
                        <div className="font-medium text-base-content truncate text-sm">
                          {p.name}
                        </div>
                        <div className="text-[11px] text-base-content/55 capitalize inline-flex items-center gap-1.5 flex-wrap">
                          <span className="badge badge-ghost badge-xs">
                            {p.type === "client" ? "cliente" : "interno"}
                          </span>
                          <span>{p.status}</span>
                          <span className="text-primary">· {p.roleInProject}</span>
                        </div>
                      </div>
                    </div>
                    <ArrowUpRight className="size-4 text-base-content/40 shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <div className="pt-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 inline-flex items-center gap-2 mb-2">
              <span className="size-1.5 rounded-full bg-accent" aria-hidden />
              Standups recientes
            </h3>
            {recentStandups.length === 0 ? (
              <div className="card-elevated p-5 text-sm text-base-content/55 text-center">
                Sin standups recientes.
              </div>
            ) : (
              <ul className="card-elevated divide-y divide-base-300/60">
                {recentStandups.map((s) => (
                  <li key={s.id}>
                    <Link
                      to="/standups/$standupId"
                      params={{ standupId: s.id }}
                      className="flex items-start justify-between gap-3 px-4 py-2.5 hover:bg-base-200/60 transition-colors text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="inline-flex items-center gap-2">
                          <ClipboardList className="size-3.5 text-base-content/40" />
                          <span className="font-medium">{formatDate(s.date)}</span>
                        </div>
                        {s.preview.length > 0 && (
                          <ul className="text-xs text-base-content/60 mt-1 space-y-0.5">
                            {s.preview.map((line, i) => (
                              <li key={i} className="line-clamp-1">
                                · {line}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <ArrowUpRight className="size-4 text-base-content/40 shrink-0 mt-0.5" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="xl:col-span-3 space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 inline-flex items-center gap-2">
            <span className="size-1.5 rounded-full bg-success" aria-hidden />
            Próximos eventos
          </h3>
          {upcomingEvents.length === 0 ? (
            <div className="card-elevated p-6 text-sm text-base-content/55 text-center">
              <CalendarDays className="size-8 mx-auto mb-2 opacity-30" />
              Sin eventos próximos visibles.
            </div>
          ) : (
            <ul className="card-elevated divide-y divide-base-300/60">
              {upcomingEvents.map((e) => {
                const isGoogle = e.source === "google_calendar";
                const isBusy = e.name === "(Ocupado)";
                return (
                  <li key={e.id} className="p-4 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap text-[11px]">
                      <span className="badge badge-sm badge-ghost gap-1">
                        <Clock className="size-3" />
                        {relativeDay(e.startDate)}
                      </span>
                      <span className="text-base-content/55">
                        {formatDate(e.startDate)}
                        {e.startTime && (
                          <>
                            {" · "}
                            <span className="tabular-nums font-medium text-base-content/80">
                              {e.startTime}
                              {e.endTime && e.endTime !== e.startTime ? `–${e.endTime}` : ""}
                            </span>
                          </>
                        )}
                      </span>
                      {isGoogle && <span className="badge badge-success badge-xs">Google</span>}
                    </div>
                    <div
                      className={`font-medium leading-snug break-words ${
                        isBusy ? "text-base-content/55 italic" : ""
                      }`}
                    >
                      {e.name}
                    </div>
                    {e.notes && (
                      <p className="text-sm text-base-content/70 whitespace-pre-wrap leading-relaxed line-clamp-3">
                        {e.notes}
                      </p>
                    )}
                    {e.externalAttendees && e.externalAttendees.length > 0 && (
                      <AttendeeList attendees={e.externalAttendees} compact />
                    )}
                    <div className="flex flex-wrap gap-2">
                      {e.externalMeetUrl && (
                        <a
                          href={e.externalMeetUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-success btn-xs"
                        >
                          <Video className="size-3" /> Meet
                        </a>
                      )}
                      {e.externalHtmlLink && (
                        <a
                          href={e.externalHtmlLink}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-ghost btn-xs"
                        >
                          Calendar <ExternalLink className="size-3" />
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * Bucket de tickets para el perfil del compañero. Render compacto
 * espejo del dashboard propio, con header + lista + link a Plane.
 */
function TeamTicketBucket({
  label,
  tone,
  tickets,
  empty,
  loading = false,
}: {
  label: string;
  tone: "primary" | "warning" | "error";
  tickets: TicketLite[];
  empty: string;
  loading?: boolean;
}) {
  const accent = { primary: "bg-primary", warning: "bg-warning", error: "bg-error" }[tone];
  const SHOW_LIMIT = 4;
  return (
    <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
      <div className="flex items-center justify-between pb-2 border-b border-base-300/60">
        <div className="flex items-center gap-2">
          <span className={`size-2 rounded-full ${accent}`} aria-hidden />
          <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/70">
            {label}
          </h4>
          {loading && <span className="loading loading-spinner loading-xs text-base-content/55" />}
        </div>
        <span className="text-sm font-bold tabular-nums">{tickets.length}</span>
      </div>
      {tickets.length === 0 ? (
        <p className="pt-3 text-sm text-base-content/45 italic">{empty}</p>
      ) : (
        <ul className="divide-y divide-base-300/40">
          {tickets.slice(0, SHOW_LIMIT).map((t) => (
            <li key={t.id} className="py-2.5 flex items-center gap-2 text-sm">
              <a
                href={t.url}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 hover:text-primary transition-colors"
              >
                <div className="truncate">{t.name}</div>
                <div className="text-xs text-base-content/55 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  {t.panelProjectName ? (
                    <span className="badge badge-primary badge-xs">{t.panelProjectName}</span>
                  ) : (
                    t.projectName && (
                      <span className="badge badge-ghost badge-xs">{t.projectName}</span>
                    )
                  )}
                  {t.state && (
                    <span
                      className={`badge badge-xs ${STATE_BADGE_BY_GROUP[t.stateGroup ?? "unknown"]}`}
                    >
                      {t.state}
                    </span>
                  )}
                  {t.dueDate && <span>vence {formatDate(t.dueDate)}</span>}
                </div>
              </a>
              <a
                href={t.url}
                target="_blank"
                rel="noreferrer"
                className="text-base-content/30 hover:text-primary shrink-0"
                aria-label="Abrir en Plane"
              >
                <ExternalLink className="size-3" />
              </a>
            </li>
          ))}
          {tickets.length > SHOW_LIMIT && (
            <li className="pt-2 text-xs text-base-content/55 italic">
              +{tickets.length - SHOW_LIMIT} más
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
